import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HomeComponent } from './components/routable/home/home.component';
import { SettingsComponent } from './components/routable/settings/settings.component';
import { NavigatorComponent } from './components/fragments/navigator/navigator.component';
import { ShortcutsComponent } from './components/fragments/shortcuts/shortcuts.component';
import { AvatarComponent } from './components/fragments/avatar/avatar.component';
import { HelpComponent } from './components/fragments/help/help.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ProfileComponent } from './components/routable/profile/profile.component';
import { MatTreeModule } from '@angular/material/tree';
import { LogoComponent } from './components/fragments/logo/logo.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LoginComponent } from './components/routable/login/login.component';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { LoaderComponent } from './components/loader/loader.component';
import { LogoutComponent } from './components/routable/logout/logout.component';
import { MatMenuModule } from '@angular/material/menu';
import { DialogComponent } from './components/dialog/dialog.component';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { HttpClientModule } from '@angular/common/http';
import { AppFeedComponent } from './components/fragments/app-feed/app-feed.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { environment } from '../environments/environment';
import { LoginCallbackComponent } from './components/login-callback/login-callback.component';
import { HeatMapComponent } from './components/fragments/heat-map/heat-map.component';
import { NavletComponent } from './components/routable/navlet/navlet.component';
import { BreadcrumbsComponent } from './components/fragments/breadcrumbs/breadcrumbs.component';
import { MatListModule } from '@angular/material/list';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';
import { TextSearchFieldComponent } from './components/fragments/text-search-field/text-search-field.component';
import { AppService } from './services/app.service';
import { UsersComponent } from './components/routable/identity/users/users.component';
import { UserComponent } from './components/routable/identity/user/user.component';
import { RolesComponent } from './components/routable/identity/roles/roles.component';
import { RoleComponent } from './components/routable/identity/role/role.component';
import { NodeComponent } from './components/routable/app-core/node/node.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActionsComponent } from './components/fragments/actions/actions.component';
import { ClaimTypesComponent } from './components/routable/identity/claim-types/claim-types.component';
import { ClaimTypeComponent } from './components/routable/identity/claim-type/claim-type.component';
import { PersonComponent } from './components/routable/app-core/person/person.component';
import { PersonsComponent } from './components/routable/app-core/persons/persons.component';
import { ResourcesComponent } from './components/routable/talent-core/resources/resources.component';
import { ResourceComponent } from './components/routable/talent-core/resource/resource.component';
import { ProductionsComponent } from './components/routable/talent-core/productions/productions.component';
import { ProductionComponent } from './components/routable/talent-core/production/production.component';
import { EditorComponent } from './components/base/editor/editor.component';
import { HistoryComponent } from './components/fragments/history/history.component';
import { SearchComponent } from './components/base/search/search.component';
import { OrganizationsComponent } from './components/routable/app-core/organizations/organizations.component';
import { OrganizationComponent } from './components/routable/app-core/organization/organization.component';
import { TalentStartComponent } from './components/routable/talent-core/talent-start/talent-start.component';
import { ImageComponent } from './components/fragments/image/image.component';
import { EngagementComponent } from './components/routable/talent-core/engagement/engagement.component';
import { EngagementsComponent } from './components/routable/talent-core/engagements/engagements.component';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ChipSelectorComponent } from './components/fragments/chip-selector/chip-selector.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { ActingProfileTileComponent } from './components/fragments/talent-core/acting-profile-tile/acting-profile-tile.component';
import { ComponentDialogComponent } from './components/component-dialog/component-dialog.component';
import { DynamicHostDirective } from './dynamic-host-directive';
import { ActingProfilesComponent } from './components/routable/talent-core/acting-profiles/acting-profiles.component';
import { ActingProfileComponent } from './components/routable/talent-core/acting-profile/acting-profile.component';
import { SearchMarqueeComponent } from './components/fragments/search-marquee/search-marquee.component';
import { ResourceSearchTileComponent } from './components/fragments/talent-core/resource-search-tile/resource-search-tile.component';
import { SearchRequestsComponent } from './components/routable/app-core/search-requests/search-requests.component';
import { SearchRequestComponent } from './components/routable/app-core/search-request/search-request.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SearchTileComponent } from './components/fragments/search-tile/search-tile.component';
import { EngagementTileComponent } from './components/fragments/talent-core/engagement-tile/engagement-tile.component';
import { EngagementCardComponent } from './components/fragments/talent-core/engagement-card/engagement-card.component';
import { ShareComponent } from './components/share/share.component';
import { ShareConsoleComponent } from './components/fragments/share-console/share-console.component';
import { GroupsComponent } from './components/routable/app-core/groups/groups.component';
import { GroupComponent } from './components/routable/app-core/group/group.component';
import { TasksComponent } from './components/routable/app-core/tasks/tasks.component';
import { TaskComponent } from './components/routable/app-core/task/task.component';
import { MessageComponent } from './components/routable/messages/message/message.component';
import { MessagesComponent } from './components/routable/messages/messages/messages.component';
import { ContactsComponent } from './components/routable/app-core/contacts/contacts.component';
import { ContactComponent } from './components/routable/app-core/contact/contact.component';
import { MessageSearchTileComponent } from './components/fragments/message-search-tile/message-search-tile.component';
import { TextOrItemListSearchFieldComponent } from './components/fragments/text-or-item-list-search-field/text-or-item-list-search-field.component';
import { DateRangeFieldComponent } from './components/fragments/date-range-field/date-range-field.component';
import { PersonPreferencesComponent } from './components/fragments/person-preferences/person-preferences.component';
import { SearchPreferencesTileComponent } from './components/fragments/search-preferences-tile/search-preferences-tile.component';
import { VenuePersonSettingsComponent } from './components/routable/app-core/venue-person-settings/venue-person-settings.component';
import { TelemetorComponent } from './components/base/telemetor/telemetor.component';
import { HtmlContentComponent } from './components/routable/app-core/html-content/html-content.component';
import { HtmlContentsComponent } from './components/routable/app-core/html-contents/html-contents.component';
import { HtmlContentTileComponent } from './components/fragments/html-content-tile/html-content-tile.component';
import { EmailMessagesComponent } from './components/routable/messages/email-messages/email-messages.component';
import { EmailMessageComponent } from './components/routable/messages/email-message/email-message.component';
import { TextMessagesComponent } from './components/routable/messages/text-messages/text-messages.component';
import { TextMessageComponent } from './components/routable/messages/text-message/text-message.component';
import { HtmlContentViewerComponent } from './components/routable/app-core/html-content-viewer/html-content-viewer.component';
import { ProductComponent } from './components/routable/commerce/product/product.component';
import { ProductCardsComponent } from './components/routable/commerce/product-cards/product-cards.component';
import { ProductsComponent } from './components/routable/commerce/products/products.component';
import { ArticleCardsComponent } from './components/routable/app-core/article-cards/article-cards.component';
import { LocalDateTimePipe } from './utilities/local-date-time-pipe';
import { SubscriberComponent } from './components/fragments/subscriber/subscriber.component';
import { NgxMaskModule } from 'ngx-mask';

@NgModule({
    declarations: [
        AppComponent,
        HomeComponent,
        SettingsComponent,
        NavigatorComponent,
        ShortcutsComponent,
        BreadcrumbsComponent,
        AvatarComponent,
        HelpComponent,
        ProfileComponent,
        LogoComponent,
        LoginComponent,
        LoaderComponent,
        LogoutComponent,
        DialogComponent,
        AppFeedComponent,
        LoginCallbackComponent,
        HeatMapComponent,
        NavletComponent,
        TextSearchFieldComponent,
        UsersComponent,
        UserComponent,
        RolesComponent,
        RoleComponent,
        NodeComponent,
        ActionsComponent,
        ClaimTypesComponent,
        ClaimTypeComponent,
        PersonsComponent,
        PersonComponent,
        ResourcesComponent,
        ResourceComponent,
        ProductionsComponent,
        ProductionComponent,
        EditorComponent,
        HistoryComponent,
        SearchComponent,
        OrganizationsComponent,
        OrganizationComponent,
        TalentStartComponent,
        ImageComponent,
        EngagementComponent,
        EngagementsComponent,
        ChipSelectorComponent,
        ActingProfileTileComponent,
        ComponentDialogComponent,
        DynamicHostDirective,
        ActingProfilesComponent,
        ActingProfileComponent,
        SearchMarqueeComponent,
        ResourceSearchTileComponent,
        SearchRequestsComponent,
        SearchRequestComponent,
        SearchTileComponent,
        EngagementTileComponent,
        EngagementCardComponent,
        ShareComponent,
        ShareConsoleComponent,
        GroupsComponent,
        GroupComponent,
        TasksComponent,
        TaskComponent,
        MessageComponent,
        MessagesComponent,
        ContactsComponent,
        ContactComponent,
        MessageSearchTileComponent,
        TextOrItemListSearchFieldComponent,
        DateRangeFieldComponent,
        PersonPreferencesComponent,
        SearchPreferencesTileComponent,
        VenuePersonSettingsComponent,
        TelemetorComponent,
        HtmlContentComponent,
        HtmlContentsComponent,
        HtmlContentTileComponent,
        EmailMessagesComponent,
        EmailMessageComponent,
        TextMessagesComponent,
        TextMessageComponent,
        HtmlContentViewerComponent,
        ProductsComponent,
        ProductComponent,
        ProductCardsComponent,
        ArticleCardsComponent,
        LocalDateTimePipe,
        SubscriberComponent
    ],
    imports: [
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        MatButtonModule,
        MatIconModule,
        MatTreeModule,
        MatSlideToggleModule,
        FormsModule,
        ReactiveFormsModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatMenuModule,
        MatDialogModule,
        MatDividerModule,
        MatExpansionModule,
        HttpClientModule,
        MatProgressSpinnerModule,
        MatListModule,
        MatTabsModule,
        MatSelectModule,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        MatCheckboxModule,
        MatChipsModule,
        MatAutocompleteModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatTooltipModule,
        NgxMaskModule.forRoot()
    ],
    providers: [
        AppService,
        {
            provide: "API_BASE_URL",
            useValue: environment.apiBaseUrl
        }
    ],
    entryComponents: [ComponentDialogComponent, ActingProfileTileComponent],
    bootstrap: [AppComponent],
})

export class AppModule { }
